import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { Button, Card, Field, Select, Loading } from '../components/ui';
import { DataTable } from '../components/DataTable';

interface AuditRow {
    id: number;
    entityType: string;
    entityId: number | null;
    action: string;
    actorName: string | null;
    at: string;
    detail: string | null;
    ipAddress: string | null;
}

/** The stored action codes, in words. */
const ACTIONS: Record<string, string> = {
    Created: 'Created',
    Updated: 'Changed',
    Deleted: 'Removed',
    Approved: 'Approved an interview',
    Rejected: 'Rejected an interview',
    LoggedIn: 'Signed in',
    LoginFailed: 'Failed sign-in',
    LoggedOut: 'Signed out',
    PasswordChanged: 'Changed a password',
    SettingsChanged: 'Changed settings',
    RecordingViewed: 'Watched a recording',
    InterviewLinkIssued: 'Issued an interview link'
};

export function Audit() {
    const [action, setAction] = useState('');
    const [page, setPage] = useState(1);

    const { data, isLoading } = useQuery({
        queryKey: ['audit', action, page],
        queryFn: () => api.get<{ total: number; page: number; pageSize: number; rows: AuditRow[] }>(
            `/api/admin/audit?page=${page}${action ? `&action=${action}` : ''}`)
    });

    const pageCount = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

    return (
        <Card
            heading
            title="Activity history"
            lede="Append-only. Configuration changes, sign-ins, reviews and every recording view are recorded here and cannot be edited from the application."
        >
            <div className="mb-4 max-w-xs">
                <Field label="Filter by action">
                    <Select
                        value={action}
                        onChange={(e) => { setAction(e.target.value); setPage(1); }}
                    >
                        <option value="">Everything</option>
                        {Object.entries(ACTIONS).map(([code, label]) => (
                            <option key={code} value={code}>{label}</option>
                        ))}
                    </Select>
                </Field>
            </div>

            {isLoading ? <Loading /> : (
                <>
                    <DataTable
                        rows={data?.rows ?? []}
                        rowKey={(row) => row.id}
                        empty="Nothing recorded yet."
                        columns={[
                            {
                                header: 'When',
                                cell: (row) => new Date(row.at).toLocaleString()
                            },
                            { header: 'Who', cell: (row) => row.actorName ?? 'System' },
                            {
                                header: 'What',
                                cell: (row) => ACTIONS[row.action] ?? row.action
                            },
                            {
                                header: 'On',
                                cell: (row) => row.entityId
                                    ? `${row.entityType} #${row.entityId}`
                                    : row.entityType
                            },
                            { header: 'Detail', cell: (row) => row.detail ?? '—' },
                            { header: 'From', cell: (row) => row.ipAddress ?? '—' }
                        ]}
                    />

                    {pageCount > 1 && (
                        <div className="mt-4 flex items-center justify-between text-sm">
                            <span className="text-slate-500">
                                Page {data?.page} of {pageCount} · {data?.total} entries
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => p - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    disabled={page >= pageCount}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
